import { Grid, Rating, Stack, Typography, useTheme } from '@mui/material';
import { FC, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { AccountCircleOutlined, CheckCircleOutlined, Link, SellOutlined, Star, StarBorder } from '@mui/icons-material';
import { OpportunityStatus, OpportunityUpdateOwnerEntityRelationInput } from '../../../../../gql/graphql';
import { StatusSelect } from '../../../../../opportunities/inputs/StatusSelect';
import { graphql } from '../../../../../gql';
import { useApiMutation, useApiQuery } from '../../../../../query/hooks';
import { mutationResponseHandler } from '../../../../../utils/mutationResponseHandler';
import { Box } from '@mui/system';
import { TagsSelect } from '../../../../../form/TagsSelect';
import { NameInput } from '../../../../../form/NameInput';
import { DrawerHeader } from '../../../../../drawer/DrawerHeader';
import { isLinksJson, isOldLinksJson } from '../../../../../form/links/LinksFormInput';
import { LinkType, LinksFormInput } from '../../../../../form/links/LinksFormInput';
import { determineArrayUpdateOperation } from '../../../../../utils/determineArrayUpdateOperation';
import { MemberSelect } from '../../../../../form/MemberSelect';
import dayjs from 'dayjs';
import { useSnackbar } from '../../../../../store/snackbar';

const opportunityDrawerForm_getOpportunity = graphql(`
	query opportunityDrawerForm_getOpportunity($opportunityId: UUID!) {
		getOpportunity(by: { id: $opportunityId }) {
			name
			status
			owner {
				id
			}
			links
			tags {
				id
			}
			benefit
			createdAt
		}
	}
`);

const opportunityDrawerForm_updateOpportunity = graphql(`
	mutation opportunityDrawerForm_updateOpportunity($opportunityId: UUID!, $data: OpportunityUpdateInput!) {
		updateOpportunity(by: { id: $opportunityId }, data: $data) {
			ok
			errorMessage
		}
	}
`);

// NOTE: Create multiple instances of the same mutation to separate loading states of input components.
const useUpdateOpportunity = () => {
	const intl = useIntl();
	const { openSnackbar } = useSnackbar();

	return useApiMutation(opportunityDrawerForm_updateOpportunity, {
		onSuccess: (result) =>
			mutationResponseHandler({
				result: result.updateOpportunity.ok,
				toastMessages: {
					success: intl.formatMessage({ id: 'opportunity.detail.update.success' }),
					error: intl.formatMessage({ id: 'opportunity.detail.update.error' }),
				},
				errorMessage: result.updateOpportunity.errorMessage,
				openSnackbar,
			}),
	});
};

const defaultBenefit = 5;

type OpportunityDrawerFormProps = Readonly<{
	opportunityId: string;
}>;

export const OpportunityDrawerForm: FC<OpportunityDrawerFormProps> = ({ opportunityId }) => {
	const intl = useIntl();
	const theme = useTheme();

	const {
		data: opportunity,
		isLoading: isOpportunityLoading,
		isPending: isOpportunityPending,
	} = useApiQuery(opportunityDrawerForm_getOpportunity, {
		variables: { opportunityId },
	});

	const { mutate: updateName } = useUpdateOpportunity();
	const { mutate: updateBenefit, isPending: isUpdateBenefitPending } = useUpdateOpportunity();
	const { mutate: updateOwner, isPending: isUpdateOwnerPending } = useUpdateOpportunity();
	const { mutate: updateLinks } = useUpdateOpportunity();
	const { mutate: updateTags } = useUpdateOpportunity();
	const { mutate: updateStatus, isPending: isUpdateStatusPending } = useUpdateOpportunity();

	const handleUpdateName = useMemo(
		() => (newName: string) => {
			updateName({ opportunityId, data: { name: newName } });
		},
		[opportunityId, updateName],
	);

	const handleUpdateBenefit = (newBenefit: number) => {
		updateBenefit({ opportunityId, data: { benefit: newBenefit } });
	};

	const handleUpdateOwner = (newOwnerId: string) => {
		if (newOwnerId !== '' && newOwnerId === opportunity?.getOpportunity?.owner?.id) {
			return;
		}

		let data: OpportunityUpdateOwnerEntityRelationInput;
		if (newOwnerId === '') {
			data = { disconnect: true };
		} else {
			data = { connect: { id: newOwnerId } };
		}

		if (!isOpportunityPending) {
			updateOwner({ opportunityId, data: { owner: data } });
		}
	};

	const links = (() => {
		if (isOldLinksJson(opportunity?.getOpportunity?.links)) {
			return opportunity.getOpportunity.links.values.map((link) => ({ link, text: undefined }));
		} else if (isLinksJson(opportunity?.getOpportunity?.links)) {
			return opportunity.getOpportunity.links.values;
		}
		return null;
	})();

	const handleUpdateLinks = (newLinks: LinkType[]) => {
		if (!isOpportunityPending) {
			updateLinks({ opportunityId, data: { links: { values: newLinks } } });
		}
	};

	const handleUpdateTags = (newTags: string[]) => {
		const tagsOperations = determineArrayUpdateOperation(opportunity?.getOpportunity?.tags.map(({ id }) => id) ?? [], newTags);
		// Prevent firing empty update mutation
		if (tagsOperations.length === 0) {
			return;
		}

		if (!isOpportunityPending) {
			updateTags({ opportunityId, data: { tags: tagsOperations } });
		}
	};

	const handleUpdateStatus = (newStatus: OpportunityStatus) => {
		if (!isOpportunityPending) {
			updateStatus({
				opportunityId,
				data: { status: newStatus, solvedAt: newStatus === 'resolved' ? dayjs().format() : null },
			});
		}
	};

	return (
		<>
			<Box px={3}>
				<DrawerHeader />
				<NameInput
					value={opportunity?.getOpportunity?.name}
					isLoading={isOpportunityPending}
					memoizedOnChange={handleUpdateName}
					placeholder={intl.formatMessage({ id: 'opportunity.detail.name.placeholder' })}
				/>
			</Box>
			<Grid container alignItems={'center'} spacing={0.5} px={3} py={2}>
				<Grid item xs={4}>
					<Stack direction={'row'} gap={1} alignItems={'center'}>
						<StarBorder sx={{ fill: theme.palette.secondary.main, fontSize: 18 }} />
						<Typography variant={'h6'} color={'secondary'}>
							<FormattedMessage id={'opportunity.detail.benefit.label'} />
						</Typography>
					</Stack>
				</Grid>
				<Grid item xs={8}>
					<Rating
						value={opportunity?.getOpportunity?.benefit ?? defaultBenefit}
						precision={1}
						onChange={(_, value) => value != null && handleUpdateBenefit(Math.round(value))}
						size={'large'}
						emptyIcon={<Star style={{ color: theme.palette.secondary.light, fontSize: 30 }} />}
						disabled={isOpportunityPending || isUpdateBenefitPending}
					/>
				</Grid>
				<Grid item xs={4}>
					<Stack direction={'row'} gap={1} alignItems={'center'}>
						<CheckCircleOutlined sx={{ fill: theme.palette.secondary.main, fontSize: 18 }} />
						<Typography variant={'h6'} color={'secondary'}>
							<FormattedMessage id={'opportunity.detail.status.label'} />
						</Typography>
					</Stack>
				</Grid>
				<Grid item xs={8}>
					<StatusSelect
						loading={isOpportunityPending || isUpdateStatusPending}
						value={opportunity?.getOpportunity?.status}
						onChange={({ target: { value } }) => handleUpdateStatus(value as OpportunityStatus)}
					/>
				</Grid>
				<Grid item xs={4}>
					<Stack direction={'row'} gap={1} alignItems={'center'}>
						<AccountCircleOutlined sx={{ fill: theme.palette.secondary.main, fontSize: 18 }} />
						<Typography variant={'h6'} color={'secondary'}>
							<FormattedMessage id={'opportunity.detail.owner.label'} />
						</Typography>
					</Stack>
				</Grid>
				<Grid item xs={8}>
					<MemberSelect
						loading={isOpportunityPending || isUpdateOwnerPending}
						value={opportunity?.getOpportunity?.owner?.id ?? ''}
						onChange={({ target: { value } }) => handleUpdateOwner(value)}
						size={'small'}
					/>
				</Grid>
				<Grid item xs={4} alignSelf={'flex-start'}>
					<Stack direction={'column'} justifyContent={'center'} sx={{ height: 40 }}>
						<Stack direction={'row'} gap={1} alignItems={'center'}>
							<Link sx={{ fill: theme.palette.secondary.main, fontSize: 18 }} />
							<Typography variant={'h6'} color={'secondary'}>
								<FormattedMessage id={'opportunity.detail.links.label'} />
							</Typography>
						</Stack>
					</Stack>
				</Grid>
				<Grid item xs={8}>
					<LinksFormInput
						isLoading={isOpportunityLoading}
						value={links}
						onChange={handleUpdateLinks}
						maxNumberOfLinks={10}
						size={'small'}
					/>
				</Grid>
				<Grid item xs={4} alignSelf={'flex-start'}>
					<Stack direction={'column'} justifyContent={'center'} sx={{ height: 37 }}>
						<Stack direction={'row'} gap={1} alignItems={'center'}>
							<SellOutlined sx={{ fill: theme.palette.secondary.main, fontSize: 18 }} />
							<Typography variant={'h6'} color={'secondary'}>
								<FormattedMessage id={'opportunity.detail.tags.label'} />
							</Typography>
						</Stack>
					</Stack>
				</Grid>
				<Grid item xs={8}>
					<TagsSelect
						loading={isOpportunityPending}
						value={opportunity?.getOpportunity?.tags.map(({ id }) => id) ?? []}
						onChange={handleUpdateTags}
						size={'small'}
					/>
				</Grid>
			</Grid>
		</>
	);
};
